import axios from 'axios';
import en from './i18n/en';
import zh from './i18n/zh';

const api = axios.create({
  baseURL: '/cortex/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 360000, // 6 min — slightly longer than server's ComfyUI timeout
});

const stripAssistantId = (data = {}) => {
  const { assistant_id, ...rest } = data || {};
  return rest;
};

// Unified session token: sessionStorage is the active-session source, but a
// previously persisted token may still live in localStorage (browser restart /
// new tab). Promote it into sessionStorage so landing "Go to App" and the SPA
// agree on the same authenticated state.
export function getToken() {
  try {
    const session = sessionStorage.getItem('socioniq_token');
    if (session) return session;
    const persisted = localStorage.getItem('socioniq_token');
    if (persisted) {
      sessionStorage.setItem('socioniq_token', persisted);
      return persisted;
    }
  } catch (_) {}
  return '';
}

export function clearToken() {
  try {
    sessionStorage.removeItem('socioniq_token');
    localStorage.removeItem('socioniq_token');
  } catch (_) {}
}

// Global error localizer: when the server sends { code, params }, replace
// the raw English error text with the i18n-localized version. This catches
// every err.response.data.error reference across all pages in one place.
const DICTIONARIES = { en, zh };
function localizeError(data) {
  if (!data || !data.code) return data;
  try {
    const storedErrLang = (typeof localStorage !== 'undefined' && localStorage.getItem('socioniq_lang')) || '';
    const lang = (storedErrLang === 'zh' || storedErrLang === 'en') ? storedErrLang
      : ((navigator.language || '').startsWith('zh') ? 'zh' : 'en');
    const dict = DICTIONARIES[lang] || en;
    const errors = dict.errors || {};
    const template = errors[data.code];
    if (template) {
      const params = data.params || {};
      data.error = template.replace(/\{(\w+)\}/g, (_, k) => params[k] !== undefined ? String(params[k]) : `{${k}}`);
    }
  } catch (_) { /* keep original error on any failure */ }
  return data;
}

api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.data) localizeError(err.response.data);
    return Promise.reject(err);
  }
);

// Attach auth token to every request
api.interceptors.request.use(cfg => {
  const token = getToken();
  if (token) {
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  // Attach assistant_id for tenant data isolation (only if not already set)
  const assistantId = localStorage.getItem('active_assistant_id');
  if (assistantId && !cfg.headers['x-assistant-id']) {
    cfg.headers['x-assistant-id'] = assistantId;
  }
  // Sync UI language so the server can localize direction labels / topics
  const storedUiLang = localStorage.getItem('socioniq_lang');
  const uiLang = (storedUiLang === 'zh' || storedUiLang === 'en') ? storedUiLang
    : ((navigator.language || '').startsWith('zh') ? 'zh' : 'en');
  if (!cfg.headers['Accept-Language']) {
    cfg.headers['Accept-Language'] = uiLang === 'zh' ? 'zh-CN' : 'en-US';
  }
  if (cfg.method === 'get') {
    cfg.params = { ...cfg.params, _t: Date.now() };
  }
  // Allow callers to opt out of global error toast
  cfg.skipErrorToast = cfg.skipErrorToast || false;
  return cfg;
});

// Redirect to login on 401
let redirecting = false;
api.interceptors.response.use(
  (response) => response,
 (error) => {
    // W28 试用冻结 (时间到 / credit 耗尽): 全局限流到用量页 —
    // AuthGuard 检测 locked_reason 后会全屏覆盖毛玻璃冻结墙, 此跳转仅兜底
    const TRIAL_FROZEN_CODES = ['TRIAL_EXPIRED', 'TRIAL_CREDIT_EXHAUSTED'];
    if (error.response?.status === 403 && TRIAL_FROZEN_CODES.includes(error.response?.data?.code) && !redirecting) {
      redirecting = true;
      window.location.href = '/cortex/usage?plan=expired';
      return Promise.reject(error);
    }
    if (error.response?.status === 401 && !redirecting) {
      const token = getToken();
      if (!token) {
        // No token at all — genuine unauthenticated state, redirect
        redirecting = true;
        window.location.href = '/';
      } else {
        // Token exists — verify it's actually invalid before clearing.
        // This prevents transient 401s (e.g. during rapid assistant switching,
        // concurrent requests, or backend hiccups) from erroneously logging
        // the user out and redirecting to landing page.
        fetch('/cortex/api/auth/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        }).then(r => r.json()).then(res => {
          if (res && res.valid) {
            // Token still valid — the 401 was transient, do NOT logout
            console.warn('[Auth] Transient 401 received (token still valid), ignoring logout');
          } else {
            // Token genuinely invalid — clear and redirect
            redirecting = true;
            clearToken();
            window.location.href = '/';
          }
        }).catch(() => {
          // Verify endpoint unreachable — conservatively keep session
          // (don't logout on network error, only on confirmed invalid token)
          console.warn('[Auth] Could not verify token after 401, keeping session');
        });
      }
    }
    return Promise.reject(error);
  }
);

// Response interceptor for unified error handling
let apiErrorHandler = null;

export function setApiErrorHandler(handler) {
  apiErrorHandler = handler;
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Extract error message from server response body:
      //   1. explicit 'error' field
      //   2. empty 'error' field → check other response data keys
      //   3. no 'error' field → use full response data as JSON
      //   4. fallback to HTTP status text
      const data = error.response.data;
      let errMsg = '';
      if (data && typeof data === 'object') {
        if (data.error) {
          errMsg = data.error;
        } else {
          // Response body has no error field (e.g. { error: undefined } serialized to {})
          const keys = Object.keys(data);
          if (keys.length > 0) {
            // Try to use any other string field, or the full body
            const firstVal = data[keys[0]];
            errMsg = typeof firstVal === 'string' ? firstVal : JSON.stringify(data);
          }
        }
      } else if (data) {
        errMsg = String(data);
      }
      if (!errMsg) {
        errMsg = error.response.statusText || 'Request failed';
      }
      // Prefix with status code for easier debugging
      error.message = `[${error.response.status}] ${errMsg}`;
    } else if (error.request) {
      error.message = 'Network error — server may be unreachable';
    }

    // Trigger global error toast if handler is registered
    if (apiErrorHandler && !error.config?.skipErrorToast) {
      apiErrorHandler(error);
    }

    return Promise.reject(error);
  }
);

// ── Single-flight + 短 TTL GET 缓存 ──
// 折叠页面挂载期的重复拉取 (auth/me, assistants, wallet, settings...)。
// 高延迟链路上每个重复请求都是一次完整跨境往返 (实测 1-4s/个)。
const _inflight = new Map();
const _getCache = new Map();
const GET_CACHE_TTL_MS = 8000;
export function cachedGet(path, key = path, config) {
  // Assistant-scoped isolation (2026-08-09): most endpoints resolve the
  // persona/tenant from the ambient x-assistant-id header, so a cache key
  // without the assistant id leaks assistant A's data into assistant B
  // when switching within the TTL (Stream portrait stayed stale).
  const scopedKey = (localStorage.getItem('active_assistant_id') || '') + '|' + key;
  const now = Date.now();
  const hit = _getCache.get(scopedKey);
  if (hit && now - hit.t < GET_CACHE_TTL_MS) return Promise.resolve(hit.r);
  if (_inflight.has(scopedKey)) return _inflight.get(scopedKey);
  const p = api.get(path, config)
    .then(r => { _getCache.set(scopedKey, { t: Date.now(), r }); return r; })
    .finally(() => _inflight.delete(scopedKey));
  _inflight.set(scopedKey, p);
  return p;
}
// 数据变更后主动失效 (如模式切换 / 助理增删) — 下一次读取强制回源
export function invalidateGetCache(prefix) {
  for (const k of [..._getCache.keys()]) { if (k.startsWith(prefix)) _getCache.delete(k); }
}

// Posts
export const posts = {
  list: () => api.get('/posts'),
  get: (id) => api.get(`/posts/${id}`),
  create: (data) => api.post('/posts', stripAssistantId(data)),
  update: (id, data) => api.put(`/posts/${id}`, stripAssistantId(data)),
  delete: (id) => api.delete(`/posts/${id}`),
  publish: (id, data = {}) => api.post(`/posts/${id}/publish`, stripAssistantId(data)),
  generate: (topic, tone, language, hasImage) => api.post('/posts/generate', { topic, tone, language, hasImage }),
  generateStream: (topic, tone, language, hasImage, onChunk) => {
    // Use raw fetch for streaming SSE
    const token = getToken() || localStorage.getItem('auth_token') || '';
    const activeAssistantId = localStorage.getItem('active_assistant_id');
    const baseUrl = api.defaults?.baseURL || '';
    const headers = { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token };
    if (activeAssistantId) headers['x-assistant-id'] = activeAssistantId;
    return fetch(baseUrl + '/posts/generate-stream', {
      method: 'POST',
      headers,
      body: JSON.stringify({ topic, tone, language, hasImage }),
    }).then(async (response) => {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.delta && onChunk) onChunk(data.delta, data.content);
              if (data.content) fullContent = data.content;
              if (data.done) return fullContent;
              if (data.error) throw new Error(data.error);
            } catch(e) { if (e.message !== 'Unexpected end of JSON input') throw e; }
          }
        }
      }
      return fullContent;
    });
  },
  generateImagePrompt: (topic, style) => api.post('/posts/generate-image-prompt', { topic, style }),
  generateImage: (data) => api.post('/posts/generate-image', data),
  uploadImage: (formData) => api.post('/posts/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  uploadVideo: (formData) => api.post('/posts/upload-video', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000, // 10 min for large video uploads
  }),
};

// Replies
export const replies = {
  list: (limit = 50, filter = 'all') => api.get('/replies', { params: { limit, filter } }),
  unprocessedCount: () => api.get('/replies/unprocessed-count'),
  sync: () => api.post('/replies/sync'),
  get: (id) => api.get(`/replies/${id}`),
  generateReply: (id) => api.post(`/replies/${id}/generate-reply`),
  postReply: (id, content, language) => api.post(`/replies/${id}/post-reply`, { content, language }),
  dismiss: (id) => api.post(`/replies/${id}/dismiss`),
};

// Images
export const images = {
  generations: (limit = 20, days = 0) => api.get('/images/generations', { params: { limit, days } }),
  generation: (id) => api.get(`/images/generations/${id}`),
  local: () => api.get('/images/local'),
  imageStatus: () => api.get('/images/image-status'),
  comfyuiStatus: () => api.get('/images/comfyui-status'),
  getWorkflows: () => api.get('/images/workflows'),
  generate: (data) => api.post('/images/generate', stripAssistantId(data)),
  getStatus: (taskId) => api.get(`/images/status/${taskId}`),
  studio: (data) => api.post('/images/studio', stripAssistantId(data)),
  studioResult: (id) => api.get(`/images/studio/result/${id}`),
  studioExpandPrompt: (data) => api.post('/images/studio/expand-prompt', stripAssistantId(data)),
  cleanupGenerated: (filePath) => api.delete('/images/cleanup', { params: { filePath } }),
};

// Video generation (H3 pipeline — async, 8-10 min)
export const videos = {
  generate: (data) => api.post('/videos/generate', stripAssistantId(data), { timeout: 30000 }),
  generations: (limit = 12) => api.get('/videos/generations', { params: { limit } }),
  generation: (id) => api.get(`/videos/generations/${id}`),
};

// Settings
export const settings = {
  get: (params = {}) => cachedGet('/settings', '/settings' + JSON.stringify(stripAssistantId(params)), { params: stripAssistantId(params) }),
  updateApiKeys: (data) => api.put('/settings/api-keys', data),
  updatePersona: (data) => api.put('/settings/persona', stripAssistantId(data)),
  uploadAvatar: (formData) => api.post('/settings/avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteAvatar: () => api.delete('/settings/avatar'),
  stats: () => api.get('/settings/stats'),
  updateSetting: (key, value) => api.put(`/settings/${key}`, { value }),
  addWorkflow: (data) => api.post('/settings/workflows', data),
  removeWorkflow: (name) => api.delete(`/settings/workflows/${name}`),
  setDefaultWorkflow: (workflow) => api.put('/settings/workflows/default', { workflow }),
  restoreDefaults: () => api.post('/settings/restore-defaults'),
  testConnections: () => api.get('/settings/test-connections'),
  // Brand
  updateBrand: (data) => api.put('/settings/brand', data),
  // AI Monitor
  toggleAiMonitor: (active, interval, threshold, monitoredTopics, monitorPlatforms, qualityTier, topics, dryRun) => api.post('/settings/ai-monitor/toggle', { active, interval, threshold, monitoredTopics, monitorPlatforms, quality_tier: qualityTier, topics, dry_run: dryRun }),
  triggerAiScan: () => api.post('/settings/ai-monitor/scan'),
  getAiMonitorStatus: () => api.get('/settings/ai-monitor/status'),
  getAiWatchlist: () => api.get('/settings/ai-monitor/watchlist'),
  suggestMonitorTopics: () => api.post('/settings/ai-monitor/suggest-topics'),
  // Interaction
  getInteractionConfig: () => api.get('/settings/interaction'),
  updateInteractionConfig: (data) => api.put('/settings/interaction', data),
  runInteractionNow: () => api.post('/settings/interaction/run-now'),
  getRunNowStatus: () => api.get('/settings/interaction/run-now/status'),
  getInteractionHistory: (limit = 50, type = null) => api.get('/settings/interaction/history', { params: { limit, type } }),
  getInteractionThreads: () => api.get('/settings/interaction/threads'),
  getKnowledgeStats: () => api.get('/settings/knowledge'),
  searchKnowledge: (query, categories = [], limit = 10) => api.post('/settings/knowledge/search', { query, categories, limit }),
  addKnowledge: (data) => api.post('/settings/knowledge', data),
  deleteKnowledge: (id) => api.delete(`/settings/knowledge/${id}`),
  triggerReflection: () => api.post('/settings/knowledge/reflect'),
  updateKnowledgeSettings: (data) => api.put('/settings/knowledge/settings', data),
  resetKnowledgeBase: () => api.post('/settings/knowledge/reset'),
  getPendingKnowledge: () => api.get('/settings/knowledge/pending'),
  approveAllKnowledge: (headers = {}) => api.post('/settings/knowledge/approve-all', {}, { headers }),
  approveKnowledge: (id, headers = {}) => api.post(`/settings/knowledge/approve/${id}`, {}, { headers }),
  rejectKnowledge: (id, headers = {}) => api.delete(`/settings/knowledge/reject/${id}`, { headers }),
  getDashboardKbStats: () => api.get('/settings/knowledge/dashboard-stats'),
  importKnowledge: (text, filename) => api.post('/settings/knowledge/import', { text, filename }),
  importKnowledgeAsync: (text, filename) => api.post('/settings/knowledge/import/async', { text, filename }),
  importKnowledgeStatus: (taskId) => api.get(`/settings/knowledge/import/async/${taskId}`),
  extractFileText: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/settings/knowledge/extract-text', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  getKnowledgeFormats: () => api.get('/settings/knowledge/formats'),
  importKnowledgeFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/knowledge/import/file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  confirmImportKnowledge: (entries) => api.post('/settings/knowledge/import/confirm', { entries }),
  getSystemKnowledge: () => api.get('/settings/knowledge/system'),
  toggleSystemKnowledge: (enabled) => api.put('/settings/knowledge/system/toggle', { enabled }),
  refreshSystemKnowledge: () => api.post('/settings/knowledge/system/refresh'),
  upgradeKnowledgeTier: (tier) => api.put('/settings/knowledge/tier/upgrade', { tier }),
  getKnowledgeTier: () => api.get('/settings/knowledge/tier'),
  exportKnowledge: () => api.get('/settings/knowledge/export'),
  toggleAutoRenew: (enabled) => api.put('/settings/knowledge/tier/auto-renew', { enabled }),
  // Perplexity
  perplexitySearch: (query) => api.post('/settings/perplexity/search', { query }),
  // X Compliance
  getXComplianceStatus: () => api.get('/settings/x-compliance'),
  refreshXCompliance: () => api.post('/settings/x-compliance/refresh'),
  updateXComplianceFromWeb: () => api.post('/settings/x-compliance/update-from-web'),
  cleanupXComplianceDuplicates: () => api.post('/settings/x-compliance/cleanup-duplicates'),
  // Migration
  getMigrationInfo: () => api.get('/settings/migration/info'),
  exportData: (options) => api.post('/settings/migration/export', options),
  importData: (data) => api.post('/settings/migration/import', data),
};

export default api;

// W9 Today — 白标媒体/瓦片 URL: <img> 无法带 Authorization 头, 用 ?token= 兜底
export function todayMediaUrl(path) {
  if (!path) return null;
  const token = getToken() || '';
  const base = (api.defaults && api.defaults.baseURL) || '';
  // 服务端返回 /api/today/... — baseURL 已含 /cortex/api, 去掉重复前缀
  const clean = String(path).replace(/^\/(cortex\/)?api/, '');
  const sep = clean.includes('?') ? '&' : '?';
  return `${base}${clean}${sep}token=${encodeURIComponent(token)}`;
}

export function uiLang() {
  const stored = localStorage.getItem('socioniq_lang');
  return (stored === 'zh' || stored === 'en') ? stored : ((navigator.language || '').startsWith('zh') ? 'zh' : 'en');
}

// W9 Today — 家庭版「今日信息」接口集合
export const today = {
  summary: () => api.get('/today/summary', { params: { lang: uiLang() } }),
  getPrefs: () => api.get('/today/prefs'),
  savePrefs: (patch) => api.post('/today/prefs', patch),
  // V-CARE 关怀偏好 (问候/邮件提醒/安静时段)
  carePrefs: () => api.get('/today/care/prefs'),
  saveCarePrefs: (patch) => api.post('/today/care/prefs', patch),
  weather: () => api.get('/today/weather', { params: { lang: uiLang() } }),
  pois: (query) => api.get('/today/pois', { params: { query, lang: uiLang() } }),
  geocodeCandidates: (q) => api.get('/today/geocode-candidates', { params: { q, lang: uiLang() } }),
  // W13: lang 界面语言联动 (高德 zh_cn/en); scale=2 视网膜 (仅高德生效, 服务端忽略 OSM)
  tileUrl: (z, x, y, opts = {}) => {
    const q = new URLSearchParams();
    if (opts.lang) q.set('lang', opts.lang);
    if (opts.scale === 2) q.set('scale', '2');
    const qs = q.toString();
    return todayMediaUrl(`/api/today/tiles/${z}/${x}/${y}.png${qs ? `?${qs}` : ''}`);
  },
  recipes: (params = {}) => api.get('/today/recipes', { params: { ...params, lang: uiLang() } }),
  surprise: () => api.get('/today/recipes/surprise', { params: { lang: uiLang() } }),
  recipeDetail: (id) => api.get(`/today/recipes/${id}`, { params: { lang: uiLang() } }),
  recipeFeedback: (id, feedback) => api.post('/today/recipes/feedback', { id, feedback }),
  movies: (opts = {}) => api.get('/today/movies', { params: { lang: uiLang(), ...(opts.fresh ? { fresh: 1 } : {}) } }),
  movieFeedback: (id, feedback) => api.post('/today/movies/feedback', { id, feedback }),
  movieSearch: (q) => api.get('/today/movies/search', { params: { q, lang: uiLang() } }),
  movieDetail: (id) => api.get(`/today/movies/${encodeURIComponent(id)}`, { params: { lang: uiLang() } }),
  news: (topic) => api.get('/today/news', { params: { lang: uiLang(), ...(topic ? { topic } : {}) } }),
  ipLocation: () => api.get('/today/ip-location', { params: { lang: uiLang() } }),
  events: (type) => api.get('/today/events', { params: type ? { type } : {} }),
  addEvent: (data) => api.post('/today/events', data),
  deleteEvent: (id) => api.delete(`/today/events/${id}`),
  getMood: () => api.get('/today/mood'),
  setMood: (mood, note) => api.post('/today/mood', { mood, note }),
};

// Daily scenes
export const daily = {
  getPresets: (params = {}) => api.get('/daily/presets', { params: stripAssistantId(params) }),
  getQueueInfo: (params = {}) => api.get('/daily/queue', { params: stripAssistantId(params) }),
  getSchedules: (params = {}) => api.get('/daily/schedules', { params: stripAssistantId(params) }),
  saveSchedules: (data) => api.post('/daily/schedules', stripAssistantId(data)),
  updateSchedule: (id, data) => api.put(`/daily/schedules/${id}`, stripAssistantId(data)),
  deleteSchedule: (id, params = {}) => api.delete(`/daily/schedules/${id}`, { params: stripAssistantId(params) }),
  deleteSchedulesByRange: (data) => api.delete('/daily/schedules', { data: stripAssistantId(data) }),
  getWorkflows: () => api.get('/daily/workflows'),
  batch: (count) => api.post('/daily/batch', { count }),
  // assistantId (optional) binds generation to the page's assistant explicitly —
  // the x-assistant-id header is ambient localStorage state and can go stale
  // across tabs, which once rendered a portrait with the wrong persona.
  generatePreview: (data, assistantId) => api.post('/daily/preview', stripAssistantId(data),
    { params: assistantId ? { assistant_id: assistantId } : {} }),
  previewResult: (taskId, params = {}) => api.get(`/daily/preview-result/${taskId}`, { params: stripAssistantId(params) }),
  publishNow: (data, assistantId) => api.post('/daily/publish-now', stripAssistantId(data),
    { params: assistantId ? { assistant_id: assistantId } : {} }),
  getConfig: () => api.get('/daily/config'),
  saveConfig: (data) => api.post('/daily/config', data),
  // Custom scenes
  listCustomScenes: () => api.get('/daily/custom-scenes'),
  createCustomScene: (data) => api.post('/daily/custom-scenes', data),
  updateCustomScene: (id, data) => api.put(`/daily/custom-scenes/${id}`, data),
  deleteCustomScene: (id) => api.delete(`/daily/custom-scenes/${id}`),
};

// Radar / lead discovery
export const radar = {
  getSettings: () => cachedGet('/settings'),
  getRadarSettings: () => api.get('/radar/settings'),
  saveSettings: (d) => api.put('/radar/settings', d),
  scan: (params = {}) => api.post('/radar/scan', stripAssistantId(params)),
  scanP0: () => api.post('/radar/scan-p0'),
  scanP1: () => api.post('/radar/scan-p1'),
  scanP2: () => api.post('/radar/scan-p2', {}, { timeout: 120000 }),
  scanP3: () => api.post('/radar/scan-p3'),
  getList: (params = {}) => api.get('/radar/leads', { params: stripAssistantId(params) }),
  createLead: (data) => api.post('/radar/leads', stripAssistantId(data)),
  updateLead: (id, data) => api.put(`/radar/leads/${id}`, stripAssistantId(data)),
  clearLeads: (mode) => api.delete('/radar/leads', { params: { mode } }),
  getTemplates: (roleType) => api.get('/radar/templates', { params: { role_type: roleType } }),
  followupLead: (id) => api.post(`/radar/leads/${id}/followup`),
  // Dashboard & Analytics
  dashboard: (params = {}) => api.get('/radar/dashboard', { params: stripAssistantId(params) }),
  entityStats: (params = {}) => api.get('/radar/entity-stats', { params: stripAssistantId(params) }),
  providers: (params = {}) => api.get('/radar/providers', { params: stripAssistantId(params) }),
  providerStats: (params = {}) => api.get('/radar/providers/stats', { params: stripAssistantId(params) }),
  providerDetail: (id) => api.get(`/radar/providers/${id}`),
  providerUpdate: (id, data) => api.put(`/radar/providers/${id}`, stripAssistantId(data)),
  providerExport: () => api.get('/radar/providers/export', { responseType: 'blob' }),
  providerRebuild: (data = {}) => api.post('/radar/providers/rebuild-from-leads', stripAssistantId(data)),
  scanHistory: (limit = 20) => api.get(`/radar/scan-history?limit=${limit}`),
  getAssistantSettings: (assistantId) => api.get(`/radar/assistant-settings/${assistantId}`),
  saveAssistantSettings: (assistantId, d) => api.put(`/radar/assistant-settings/${assistantId}`, d),
};

// Auth
export const auth = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  logout: () => {
    const token = getToken();
    if (token) api.post('/auth/logout', { token });
    clearToken();
    window.location.href = '/';
  },
  verify: () => api.post('/auth/verify', { token: getToken() }),
  getMe: () => api.get('/auth/me'),
  createInvitation: (data) => api.post('/auth/invite', data),
  getInvitations: () => api.get('/auth/invitations'),
};

// Assistant / AI Profile
export const assistant = {
  getProfile: (config) => api.get('/assistant/profile', config),
  updateProfile: (data) => api.post('/assistant/profile', data),
  getDirections: (config) => api.get('/assistant/directions', config),
  getSystemPrompt: (config) => api.get('/assistant/system-prompt', config),
  // X OAuth 1.0a flow
};

// Usage / Subscription
export const usage = {
  getStats: (config) => api.get('/usage/stats', config),
  getAssistantStats: (assistantId) => api.get('/usage/assistant-stats', { params: { assistant_id: assistantId } }),
  getHistory: () => api.get('/usage/history'),
  getTierInfo: () => api.get('/usage/tier-info'),
};

// Assistants (multi-assistant management)
export const assistants = {
  list: () => cachedGet('/assistants'),
  get: (id) => api.get('/assistants/' + id),
  create: (data) => api.post('/assistants', data).then(r => { invalidateGetCache('/assistants'); return r; }),
  update: (id, data) => api.put('/assistants/' + id, data).then(r => { invalidateGetCache('/assistants'); return r; }),
  archive: (id) => api.delete('/assistants/' + id).then(r => { invalidateGetCache('/assistants'); return r; }),
  setDefault: (id) => api.put('/assistants/' + id + '/set-default').then(r => { invalidateGetCache('/assistants'); return r; }),
  byPublic: (publicId) => cachedGet('/assistants/by-public/' + publicId),
};

// Export (CSV / JSON download)
export const exportData = {
  leads: (format = 'csv', assistantId = null) => {
    const params = { format };
    if (assistantId) params.assistant_id = assistantId;
    return api.get('/export/leads', { params, responseType: 'blob' });
  },
  providers: (format = 'csv', assistantId = null) => {
    const params = { format };
    if (assistantId) params.assistant_id = assistantId;
    return api.get('/export/providers', { params, responseType: 'blob' });
  },
};

// Conversations (conversation history)
export const conversations = {
  list: (params) => api.get('/assistant/conversations', { params }),
  get: (id, params) => api.get(`/assistant/conversations/${id}`, { params }),
  create: (data) => api.post('/assistant/conversations', data),
  update: (id, data) => api.put(`/assistant/conversations/${id}`, data),
  delete: (id, params) => api.delete(`/assistant/conversations/${id}`, { params }),
  addMessage: (id, data) => api.post(`/assistant/conversations/${id}/message`, data),
  chat: (id, data) => api.post(`/assistant/conversations/${id}/chat`, data),
};
export const voice = {
  capabilities: () => api.get('/assistant/voice-capabilities'),
  transcribe: (audioBlob, language) => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'voice.webm');
    if (language) formData.append('language', language);
    return api.post('/assistant/transcribe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
};
export const postLog = {
  list: (params = {}) => api.get('/post-log', { params: stripAssistantId(params) }),
  upcoming: (params = {}) => api.get('/post-log/upcoming', { params }),
};
export const campaigns = {
  list: () => api.get('/campaigns'),
  create: (data) => api.post('/campaigns', data),
  get: (id) => api.get(`/campaigns/${id}`),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  remove: (id) => api.delete(`/campaigns/${id}`),
  uploadMaterials: (id, formData) => api.post(`/campaigns/${id}/materials`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000,
  }),
  updateMaterial: (id, mid, data) => api.put(`/campaigns/${id}/materials/${mid}`, data),
  deleteMaterial: (id, mid) => api.delete(`/campaigns/${id}/materials/${mid}`),
  uploadImages: (id, formData) => api.post(`/campaigns/${id}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  addGlossary: (id, data) => api.post(`/campaigns/${id}/glossary`, data),
  removeGlossary: (id, gid) => api.delete(`/campaigns/${id}/glossary/${gid}`),
  updateGlossary: (id, gid, data) => api.put(`/campaigns/${id}/glossary/${gid}`, data),
  generate: (id, data = {}) => api.post(`/campaigns/${id}/generate`, data, { timeout: 300000 }),
  generateProgress: (id) => api.get(`/campaigns/${id}/generate-progress`),
  posts: (id) => api.get(`/campaigns/${id}/posts`),
  updatePost: (id, pid, data) => api.put(`/campaigns/${id}/posts/${pid}`, data),
  reorderPosts: (id, slot_order) => api.post(`/campaigns/${id}/posts/reorder`, { slot_order }),
  approvePost: (id, pid) => api.post(`/campaigns/${id}/posts/${pid}/approve`),
  approveAll: (id) => api.post(`/campaigns/${id}/posts/approve-all`),
};
